import {
  EstateImprovement,
  EstateSubscription,
} from "@1games-ru/region-shared/dist/enums/estate/estate";
import { EstateSubscriptionDto } from "@1games-ru/region-shared/dist/types/estate/estate";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider.ts";
import useAppSelector from "@/core/helpers/useAppSelector.ts";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { estateSelector } from "@/domains/estate/slice.ts";
import toast from "@/domains/toasts/helpers/toast.ts";

export type Improvement = {
  type: EstateImprovement;
  defaultValue: number;
  minValue: number;
  maxValue: number;
  currentValue: number;
  nextValuePrice: {
    value: number;
    amount: number;
    materials: number;
  } | null;
  prices: {
    value: number;
    amount: number;
    materials: number;
  }[];
};

export default () => {
  const { t, i18n } = useTranslation("estate");
  const estate = useAppSelector(estateSelector);

  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [improvements, setImprovements] = useState<Improvement[]>([]);
  const [subscriptions, setSubscriptions] = useState<EstateSubscriptionDto[]>([]);
  const [selectedImprovementType, setSelectedImprovementType] = useState<EstateImprovement | null>(
    null,
  );
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState<EstateSubscription | null>(null);
  const [isStartLoading, setIsStartLoading] = useState(false);
  const [isEndLoading, setIsEndLoading] = useState(false);

  const selectedImprovement = useMemo(
    () => improvements.find((i) => i.type === selectedImprovementType) ?? null,
    [selectedImprovementType, improvements],
  );

  const selectedSubscription = useMemo(
    () => subscriptions.find((i) => i.type === selectedSubscriptionType) ?? null,
    [subscriptions, selectedSubscriptionType],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [improvementsResponse, subscriptionsResponse] = await Promise.all([
        EstateMpProvider.getImprovements(estate.id),
        EstateMpProvider.getSubscriptions(estate.id),
      ]);

      const hasImprovementsError = improvementsResponse instanceof RpcMpProviderFailedResponse;
      const hasSubscriptionsError = subscriptionsResponse instanceof RpcMpProviderFailedResponse;

      if (hasImprovementsError || hasSubscriptionsError) {
        if (hasImprovementsError) {
          if (i18n.exists(`estate:estate.errors.${improvementsResponse.error.message}`)) {
            toast.error(t(`estate.errors.${improvementsResponse.error.message}`));
          } else {
            toast.error(t("estate.errors.notLoadData"));
          }
          console.error("Failed to load improvements:", improvementsResponse);
        }

        if (hasSubscriptionsError) {
          if (i18n.exists(`estate:estate.errors.${subscriptionsResponse.error.message}`)) {
            toast.error(t(`estate.errors.${subscriptionsResponse.error.message}`));
          } else {
            toast.error(t("estate.errors.notLoadData"));
          }
          console.error("Failed to load subscriptions:", subscriptionsResponse);
        }

        setImprovements([]);
        setSubscriptions([]);
        return;
      }

      setSubscriptions(subscriptionsResponse.data);
      setImprovements(improvementsResponse.data);
    } catch (error) {
      console.error("Unexpected error loading data:", error);
      toast.error(t("estate.errors.notLoadData"));
      setImprovements([]);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [estate.id, i18n, t]);

  useLayoutEffect(() => {
    loadData();
  }, [loadData]);

  const loadSubscribeStart = useCallback(
    async (type: EstateSubscription) => {
      setIsStartLoading(true);
      try {
        const response = await EstateMpProvider.getSubscribeStart(estate.id, type);

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`estate:estate.errors.${response.error.message}`)) {
            toast.error(t(`estate.errors.${response.error.message}`));
          } else {
            toast.error(t("estate.errors.notLoadData"));
          }
          console.error("Failed to load getSubscribeStart:", response);
          return;
        }

        await loadData();
      } catch (error) {
        console.error("Unexpected error loading subscribe start:", error);
        toast.error(t("estate.errors.notLoadData"));
      } finally {
        setIsStartLoading(false);
      }
    },
    [estate.id, i18n, loadData, t],
  );

  const loadSubscribeEnd = useCallback(
    async (type: EstateSubscription) => {
      setIsEndLoading(true);
      try {
        const response = await EstateMpProvider.getSubscribeEnd(estate.id, type);

        if (response instanceof RpcMpProviderFailedResponse) {
          if (i18n.exists(`estate:estate.errors.${response.error.message}`)) {
            toast.error(t(`estate.errors.${response.error.message}`));
          } else {
            toast.error(t("estate.errors.notLoadData"));
          }
          console.error("Failed to load getSubscribeEnd:", response);
          return;
        }
        await loadData();
      } catch (error) {
        console.error("Unexpected error loading subscribe end:", error);
        toast.error(t("estate.errors.notLoadData"));
      } finally {
        setIsEndLoading(false);
      }
    },
    [estate.id, i18n, loadData, t],
  );

  const buy = useCallback(async () => {
    if (pending || !selectedImprovement) {
      return;
    }
    setPending(true);
    try {
      const response = await EstateMpProvider.buyImprove(estate.id, selectedImprovement.type);

      if (response instanceof RpcMpProviderFailedResponse) {
        console.error(response);
        toast.error(t(`estate.errors.${response.error.message}`));
        return;
      }

      await loadData();
    } finally {
      setPending(false);
    }
  }, [t, estate.id, pending, selectedImprovement, loadData]);

  return {
    improvements,
    subscriptions,
    selectedImprovement,
    setSelectedImprovementType,
    selectedSubscription,
    setSelectedSubscriptionType,
    buy,
    pending,
    loading,
    isStartLoading,
    isEndLoading,
    loadSubscribeStart,
    loadSubscribeEnd,
  };
};
